'use client';

import { FEATURE_STATS_ENABLED } from '@/lib/features';
import type { TalleristaRow } from '@/lib/supabase/types';
import { AppIcon, AppImage } from '@impacta/shared';
import Link from 'next/link';
import type { FAQItem } from '@/data/parseFaqs';
import FaqAccordion from '@/components/FaqAccordion';

interface Props {
  talleristas: TalleristaRow[];
  talleristasFaqs: FAQItem[];
}

const steps = [
  {
    number: '01',
    label: 'Expresión',
    title: 'Expresión de interés',
    description:
      'Completa este breve formulario cuéntanos sobre ti, tu área de expertise, experiencia y la idea de Actividad que tienes en mente. Sin compromisos, solo queremos saber quién eres.',
    icon: 'PencilSquareIcon',
    color: 'rgba(97,33,102,0.15)',
    borderColor: 'rgba(97,33,102,0.35)',
    accentColor: '#c084cc',
  },
  {
    number: '02',
    label: 'Evaluación',
    title: 'Evaluación del perfil',
    description:
      'Nuestro equipo revisa tu postulación en un plazo de 5 días hábiles. Evaluamos experiencia, propuesta pedagógica y alineación con nuestra comunidad.',
    icon: 'ClipboardDocumentCheckIcon',
    color: 'rgba(0,161,155,0.1)',
    borderColor: 'rgba(0,161,155,0.3)',
    accentColor: '#00A19B',
  },
  {
    number: '03',
    label: 'Formación',
    title: 'Formación y onboarding',
    description:
      'Accedes a nuestra guía de diseño pedagógico, sesión de onboarding con el equipo y recursos para estructurar tu taller de manera efectiva. Afinamos la idea en conjunto.',
    icon: 'AcademicCapIcon',
    color: 'rgba(97,33,102,0.15)',
    borderColor: 'rgba(97,33,102,0.35)',
    accentColor: '#c084cc',
  },
  {
    number: '04',
    label: 'Publicación',
    title: 'Publicación y lanzamiento',
    description:
      'Tu taller se publica en la plataforma con fotografía profesional, descripción optimizada y difusión en nuestra red. Tú enseñas, nosotros llenamos las plazas.',
    icon: 'RocketLaunchIcon',
    color: 'rgba(0,161,155,0.1)',
    borderColor: 'rgba(0,161,155,0.3)',
    accentColor: '#00A19B',
  },
];

const phases = [
  {
    phase: 'Fase 1',
    title: 'Postulación abierta',
    description:
      'Cualquier persona con conocimiento valioso puede postular. No necesitas ser docente certificado ni tener experiencia previa en plataformas. Solo necesitas saber algo que valga la pena enseñar y ganas de compartirlo.',
    icon: 'UserPlusIcon',
    items: [
      'Formulario en línea (10 min)',
      'Sin costo de postulación',
      'Respuesta en 5 días hábiles',
    ],
  },
  {
    phase: 'Fase 2',
    title: 'Comunidad verificada',
    description:
      'Aquí no solo publicas una actividad. Una vez aprobado, formas parte de una comunidad curada de instructores. Te acompañamos en la difusión de tu actividad y en el cuidado de la experiencia completa. Accedes a herramientas de gestión, soporte pedagógico continuo y una red de pares con quienes colaborar y crecer.',
    icon: 'ShieldCheckIcon',
    items: [
      'Panel de gestión de actividades',
      'Soporte pedagógico continuo',
      'Red de talleristas activos',
      'Condiciones claras y trato justo',
    ],
  },
];

export default function TalleristasPageClient({ talleristas, talleristasFaqs }: Props) {
  return (
    <div className="pt-16">
      {/* Hero / Intro */}
      <section
        className="relative overflow-hidden py-24 lg:py-32"
        style={{ backgroundColor: '#1e0b20' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 60% 40%, rgba(97,33,102,0.5) 0%, transparent 60%), radial-gradient(ellipse at 10% 80%, rgba(0,161,155,0.15) 0%, transparent 50%)',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-8 border"
            style={{
              borderColor: 'rgba(97,33,102,0.5)',
              color: '#c084cc',
              backgroundColor: 'rgba(97,33,102,0.15)',
            }}
          >
            <AppIcon name="AcademicCapIcon" size={14} />
            Para talleristas
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
            Comparte lo que sabes.
            <br />
            <span style={{ color: '#c084cc' }}>Inspira a quien aprende.</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10">
            IMPACTA.red conecta especialistas apasionados que quieran crear experiencias que sí
            importan con profesionales que buscan aprender haciendo. Si tienes una idea que invite a
            reflexionar y crear en conjunto, este es tu lugar.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/postular"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all duration-200 bg-[#612166] text-white border-2 border-[rgba(192,132,204,0.3)] hover:bg-[#7d2b82] hover:scale-105"
            >
              Iniciar expresión de interés
              <AppIcon name="ArrowRightIcon" size={18} />
            </Link>
            <a
              href="#comunidad"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all duration-200 bg-white/10 text-white border border-white/40 hover:bg-white/20"
            >
              Ver comunidad
              <AppIcon name="UserGroupIcon" size={18} />
            </a>
          </div>

          {FEATURE_STATS_ENABLED && (
            <div className="grid grid-cols-3 gap-8 mt-16 pt-12 border-t border-white/10">
              {[
                { value: '68', label: 'Talleristas activos' },
                { value: '2.400+', label: 'Estudiantes formados' },
                { value: '4.9★', label: 'Calificación promedio' },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-3xl md:text-4xl font-extrabold text-white mb-1">{s.value}</p>
                  <p className="text-xs text-white/70 uppercase tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4-Step Application Flow */}
      <section className="py-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4 bg-[#612166]/10 text-[#612166] border border-[#612166]/20">
              <AppIcon name="MapIcon" size={14} />
              Proceso de postulación
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#373A36] mb-4">
              Cuatro pasos hacia tu Actividad
            </h2>
            <p className="text-[#6b6e68] text-lg max-w-2xl mx-auto">
              Un proceso diseñado para ser simple, transparente y acompañado en cada etapa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div
                key={step.number}
                className="relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{
                  background: step.color,
                  border: `1px solid ${step.borderColor}`,
                }}
              >
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-3 w-6 h-0.5 bg-[#E8E8E4] z-10" />
                )}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: step.color, border: `1px solid ${step.borderColor}` }}
                >
                  <AppIcon name={step.icon as any} size={22} style={{ color: step.accentColor }} />
                </div>
                <span
                  className="text-xs font-black uppercase tracking-widest mb-2 block"
                  style={{ color: step.accentColor }}
                >
                  {step.number} — {step.label}
                </span>
                <h3 className="text-[#373A36] font-bold text-lg mb-3">{step.title}</h3>
                <p className="text-[#6b6e68] text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2-Phase Explanation */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#373A36] mb-4">
              Cómo funciona la postulación
            </h2>
            <p className="text-[#6b6e68] text-lg max-w-xl mx-auto">
              Dos fases que te llevan desde la idea hasta el lanzamiento de tu primera Actividad
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {phases.map((phase, idx) => (
              <div
                key={phase.phase}
                className="rounded-3xl p-10 transition-all duration-300 hover:shadow-xl"
                style={{
                  background: idx === 0 ? '#1e0b20' : '#F9F9F7',
                  border: idx === 0 ? '1px solid rgba(97,33,102,0.3)' : '1px solid #E8E8E4',
                }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: idx === 0 ? 'rgba(97,33,102,0.3)' : 'rgba(97,33,102,0.1)',
                  }}
                >
                  <AppIcon
                    name={phase.icon as any}
                    size={26}
                    style={{ color: idx === 0 ? '#c084cc' : '#612166' }}
                  />
                </div>
                <span
                  className="text-xs font-black uppercase tracking-widest mb-2 block"
                  style={{ color: idx === 0 ? '#c084cc' : '#612166' }}
                >
                  {phase.phase}
                </span>
                <h3
                  className="text-2xl font-extrabold mb-4"
                  style={{ color: idx === 0 ? '#ffffff' : '#373A36' }}
                >
                  {phase.title}
                </h3>
                <p
                  className="text-base leading-relaxed mb-6"
                  style={{ color: idx === 0 ? 'rgba(255,255,255,0.65)' : '#6b6e68' }}
                >
                  {phase.description}
                </p>
                <ul className="space-y-3">
                  {phase.items.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor:
                            idx === 0 ? 'rgba(97,33,102,0.4)' : 'rgba(97,33,102,0.12)',
                        }}
                      >
                        <AppIcon
                          name="CheckIcon"
                          size={12}
                          style={{ color: idx === 0 ? '#c084cc' : '#612166' }}
                        />
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: idx === 0 ? 'rgba(255,255,255,0.8)' : '#373A36' }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Grid */}
      <section id="comunidad" className="py-24 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4 bg-[#612166]/10 text-[#612166] border border-[#612166]/20">
              <AppIcon name="UserGroupIcon" size={14} />
              Nuestra comunidad
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#373A36] mb-4">
              Conoce a nuestros Talleristas
            </h2>
            <p className="text-[#6b6e68] text-lg max-w-2xl mx-auto">
              Especialistas verificados con experiencia real, comprometidos con la calidad y el
              aprendizaje significativo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {talleristas.map((teacher) => (
              <div
                key={teacher.name}
                className="group bg-white rounded-3xl overflow-hidden border border-[#E8E8E4] transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Photo */}
                  <div className="sm:w-40 sm:flex-shrink-0 relative overflow-hidden">
                    <div className="w-full h-48 sm:h-full min-h-[180px] relative">
                      {teacher.photo_url?.startsWith('http') ? (
                        <AppImage
                          src={teacher.photo_url}
                          alt={teacher.photo_alt ?? `${teacher.name}, tallerista IMPACTA.red`}
                          fill
                          className="object-cover transition-transform duration-300 group-hover:scale-105"
                          sizes="(max-width: 640px) 100vw, 160px"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#e6e6e6] flex items-center justify-center">
                          <AppIcon name="UserIcon" size={48} className="text-[#6b6e68]" />
                        </div>
                      )}
                    </div>
                    {/* Purple overlay on hover */}
                    <div className="absolute inset-0 bg-[#612166]/0 group-hover:bg-[#612166]/15 transition-all duration-300" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-[#373A36] font-extrabold text-lg leading-tight">
                          {teacher.name}
                        </h3>
                        <span
                          className="inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-bold"
                          style={{ backgroundColor: 'rgba(97,33,102,0.1)', color: '#612166' }}
                        >
                          {teacher.specialty}
                        </span>
                      </div>
                      {teacher.specialty && (
                        <div className="flex items-center gap-1 text-xs text-[#6b6e68] flex-shrink-0 ml-2">
                          <AppIcon name="AcademicCapIcon" size={14} style={{ color: '#612166' }} />
                          <span className="font-bold text-[#612166]">{teacher.specialty}</span>
                        </div>
                      )}
                    </div>

                    <p className="text-[#6b6e68] text-sm leading-relaxed mb-5 line-clamp-4">
                      {teacher.bio}
                    </p>

                    <Link
                      href={`/talleristas/${teacher.slug}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 bg-[#612166]/10 text-[#612166] border border-[#612166]/20 hover:bg-[#612166] hover:text-white"
                    >
                      Ver perfil
                      <AppIcon name="ArrowRightIcon" size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main CTA */}
      <section
        id="postular"
        className="py-24 relative overflow-hidden"
        style={{ backgroundColor: '#1e0b20' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 50%, rgba(97,33,102,0.55) 0%, transparent 65%)',
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-8"
            style={{
              backgroundColor: 'rgba(97,33,102,0.35)',
              border: '1px solid rgba(192,132,204,0.3)',
            }}
          >
            <AppIcon name="RocketLaunchIcon" size={36} style={{ color: '#c084cc' }} />
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            ¿Listo para compartir
            <br />
            <span style={{ color: '#c084cc' }}>lo que sabes?</span>
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            De seguro hay muchas personas interesadas en aprender contigo. Únete a la Comunidad de
            Talleristas de IMPACTA.red que están transformando su conocimiento en experiencias
            memorables. El primer paso toma menos de 10 minutos.
          </p>
          <Link
            href="/postular"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full font-bold text-lg transition-all duration-200 bg-[#612166] text-white border-2 border-[rgba(192,132,204,0.3)] hover:bg-[#7d2b82] hover:scale-105 hover:shadow-2xl"
          >
            Iniciar expresión de interés
            <AppIcon name="ArrowRightIcon" size={20} />
          </Link>
          <p className="mt-6 text-white/70 text-sm">
            Sin costo · Respuesta en 5 días hábiles · Proceso 100% en línea
          </p>
        </div>
      </section>

      {talleristasFaqs.length > 0 && (
        <FaqAccordion
          faqs={talleristasFaqs}
          accentColor="#612166"
          accentLight="rgba(97,33,102,0.1)"
          accentBg="rgba(97,33,102,0.05)"
          accentBorder="rgba(97,33,102,0.15)"
          cta={
            <>
              <p className="text-[#373A36] font-semibold mb-2">Tienes mas preguntas?</p>
              <p className="text-[#6b6e68] text-sm mb-5">
                Nuestro equipo esta disponible para ayudarte en todo momento.
              </p>
              <Link
                href="/ayuda"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-200 hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{ background: '#612166', color: 'white', outlineColor: '#612166' }}
              >
                <AppIcon name="QuestionMarkCircleIcon" size={16} />
                Ir al centro de ayuda
              </Link>
            </>
          }
        />
      )}
    </div>
  );
}
